import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { FieldArray, useFormikContext } from "formik"
import { faCopy } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrashCan, faCirclePlus } from "@fortawesome/pro-solid-svg-icons"
import {
    Box,
    Grid,
    IconButton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { FieldsParams } from "../../../../createOrder/components/form/formTypes"
import { StandardButton } from "../../../../buttons/StandardButton"
import { CreateUpdateAttendeesFormValues } from "./attendeesTypes"
import { attendeeInitValues } from "./CreateUpdateAttendeesForm"
import { FormikTextField } from "../../../../forms/Formik"
import { AttendeesDrawer } from "./AttendeesDrawer"

const useStyles = makeStyles()((theme) => ({
    tableContainer: {
        borderRadius: ".75rem",
        overflow: "hidden",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        "& .MuiFormHelperText-root": {
            position: "absolute",
            bottom: "0",
            transform: "translateY(100%)",
        },
    },
    table: {
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0",
        "& .MuiTableCell-root": {
            border: "none",
            padding: ".75rem 1rem",
            color: theme.palette.tailwind.slate[500],
        },
    },
    tableHead: {
        background: theme.palette.tailwind.slate[50],
        "& .MuiTableRow-root": {
            "& >th:first-of-type": {
                borderTopLeftRadius: ".75rem",
            },
            "& >th:last-of-type": {
                borderTopRightRadius: ".75rem",
            },
        },
        "& .MuiTableCell-root": {
            borderBottomColor: theme.palette.tailwind.slate[100],
        },
    },
    tableRowLast: {
        "& > td:first-of-type": {
            borderBottomLeftRadius: ".75rem",
        },
        "& > td:last-of-type": {
            borderBottomRightRadius: ".75rem",
        },
    },
    textAlignCenter: {
        textAlign: "center",
    },
    buttonWrap: {
        textAlign: "center",
        paddingTop: "1.5rem",
    },
    button: {
        minWidth: "15rem",
    },
    trashButton: {
        padding: 0,
        color: theme.palette.tailwind.red[400],
    },
    blackButton: {
        color: theme.palette.tailwind.slate[800],
    },
}))

type CreateUpdateAttendeesFormContentProps = Readonly<{
    submitButtonText: string
    fieldsParams?: FieldsParams[]
}>

export function CreateUpdateAttendeesFormContent({
    submitButtonText,
    fieldsParams,
}: CreateUpdateAttendeesFormContentProps) {
    const { classes } = useStyles()
    const [showDrawer, setShowDrawer] = useState(false)
    const findParam = (fieldKey: string) => fieldsParams?.find((p) => p.label.toLowerCase() === fieldKey)

    const { values, handleChange, handleSubmit } = useFormikContext<CreateUpdateAttendeesFormValues>()

    const handleCloseDrawer = () => {
        setShowDrawer(false)
    }

    return (
        <>
            <Stack gap="1.5rem" width="100%">
                <Stack flexDirection="row" justifyContent="flex-end">
                    <LocalizedStrict
                        id="create-order-confirm-booking-dialog-bulk-add-attendees-button-tooltip"
                        attrs={{ title: true }}
                    >
                        <Tooltip title="Add multiple participants by pasting a list of names and companies">
                            <Box>
                                <LocalizedStrict id="create-order-confirm-booking-dialog-bulk-add-attendees-button">
                                    <StandardButton
                                        variant="outlined"
                                        size="small"
                                        fullWidth={false}
                                        onClick={() => setShowDrawer(true)}
                                        startIcon={<FontAwesomeIcon icon={faCopy} size="xs" />}
                                    >
                                        Copy and paste
                                    </StandardButton>
                                </LocalizedStrict>
                            </Box>
                        </Tooltip>
                    </LocalizedStrict>
                </Stack>
                <Grid container direction="column" className={classes.tableContainer}>
                    <Table className={classes.table}>
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <LocalizedStrict id="create-order-participants-dialog-name-column">
                                    <TableCell>Name</TableCell>
                                </LocalizedStrict>
                                <LocalizedStrict id="create-order-participants-dialog-company-column">
                                    <TableCell>Company</TableCell>
                                </LocalizedStrict>
                                {/* <LocalizedStrict id="create-order-participants-dialog-email-column">
                                    <TableCell>Email</TableCell>
                                </LocalizedStrict> */}
                                <LocalizedStrict id="create-order-participants-dialog-external-internal-column">
                                    <TableCell className={classes.textAlignCenter}>External</TableCell>
                                </LocalizedStrict>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <FieldArray name="attendees">
                            {({ push, remove }) => (
                                <TableBody>
                                    {values.attendees.map((_attendee, index) => {
                                        return (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <LocalizedStrict
                                                        id="create-order-participants-dialog-field-name"
                                                        attrs={{ placeholder: true }}
                                                    >
                                                        <FormikTextField
                                                            name={`attendees[${index}].name`}
                                                            placeholder="Participant name"
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{
                                                                maxLength:
                                                                    findParam("name")?.inputField?.maxLenght ?? 20,
                                                            }}
                                                        />
                                                    </LocalizedStrict>
                                                </TableCell>

                                                <TableCell>
                                                    <LocalizedStrict
                                                        id="create-order-participants-dialog-field-company"
                                                        attrs={{ placeholder: true }}
                                                    >
                                                        <FormikTextField
                                                            name={`attendees[${index}].company`}
                                                            placeholder="Company"
                                                            fullWidth
                                                            size="small"
                                                            inputProps={{
                                                                maxLength:
                                                                    findParam("company")?.inputField?.maxLenght ?? 50,
                                                            }}
                                                        />
                                                    </LocalizedStrict>
                                                </TableCell>

                                                {/* <TableCell>
                                                    <LocalizedStrict
                                                        id="create-order-participants-dialog-field-email"
                                                        attrs={{ placeholder: true }}
                                                    >
                                                        <FormikTextField
                                                            name={`attendees[${index}].email`}
                                                            placeholder="Email address (optional)"
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    </LocalizedStrict>
                                                </TableCell> */}

                                                <TableCell className={classes.textAlignCenter}>
                                                    <Switch
                                                        name={`attendees[${index}].isExternal`}
                                                        checked={values.attendees[index].isExternal}
                                                        onChange={(event) => {
                                                            handleChange({
                                                                target: {
                                                                    name: `attendees[${index}].isExternal`,
                                                                    value: event.target.checked,
                                                                },
                                                            })
                                                        }}
                                                    />
                                                </TableCell>

                                                <TableCell className={classes.textAlignCenter}>
                                                    <IconButton
                                                        onClick={() => remove(index)}
                                                        className={classes.trashButton}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashCan} size="xs" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                    <TableRow>
                                        <TableCell>
                                            <LocalizedStrict id="create-order-confirm-booking-dialog-add-attendee-button">
                                                <StandardButton
                                                    variant="text"
                                                    size="small"
                                                    fullWidth={false}
                                                    className={classes.blackButton}
                                                    onClick={() => push({ ...attendeeInitValues })}
                                                    startIcon={<FontAwesomeIcon icon={faCirclePlus} size="xs" />}
                                                >
                                                    Add participants
                                                </StandardButton>
                                            </LocalizedStrict>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </FieldArray>
                    </Table>
                </Grid>
            </Stack>
            <Box className={classes.buttonWrap}>
                <StandardButton
                    fullWidth={false}
                    updateDefaultClasses={classes.button}
                    onClick={() => handleSubmit()}
                    type="button"
                >
                    {submitButtonText}
                </StandardButton>
            </Box>
            {showDrawer && <AttendeesDrawer showDrawer={showDrawer} handleClose={handleCloseDrawer} />}
        </>
    )
}
